/* eslint-disable max-lines-per-function */
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import createMuiTheme from '@material-ui/core/styles/createTheme';
import TextField from '@material-ui/core/TextField';
import Autocomplete, {createFilterOptions} from '@material-ui/lab/Autocomplete';
import PropTypes from 'prop-types';
/* eslint-disable no-use-before-define */
import React, {useEffect} from 'react';
import {connect} from 'react-redux';
import {Notification} from '../classes/Notification';
import locale from '../locale/Localization';
import {add, get} from '../script/notarized-objects-service';
import {addNotifications} from '../store/appActions';
import ContractRadio from './ContractRadio';
import NotarizedTypeInput from './NotarizedTypeInput';

const mapStateToProps = (state) => {
  return {
    user: state.user
  };
};

/**
 * Map the properties.
 * @param {*} dispatch
 * @return {Function} the add user function.
 */
function mapDispatchToProps(dispatch) {
  return {
    dispatch: dispatch
  };
}

const filter = createFilterOptions();

const theme = createMuiTheme();
const styles = {
  textField: {
    marginRight: theme.spacing(1),
    width: 250,
    marginLeft: 10,
    paddingBottom: 15
  }
};

/**
 * Notarized Type autocomplete field.
 * @param {P} props the props.
 * @return {*} the rendered string.
 */
const ConnectedNotarizedObjectInput = (props) => {
  const [value, setValue] = React.useState(props.value);
  const [open, toggleOpen] = React.useState(false);
  const [notarizedObjects, setNotarizedObjects] = React.useState(false);

  const addNotarizedObjectNotifications = (notifications) =>
    props.dispatch(addNotifications(notifications));

  useEffect(() => {
    if (props.user.firstNames !== 'Guest') {
      get().then((response) => {
        setNotarizedObjects(response);
      });
    }
  }, []);

  const handleClose = () => {
    setDialogValue({
      notarizedObject: '',
      notarizedType: '',
      contract: ''
    });
    toggleOpen(false);
  };

  const [dialogValue, setDialogValue] = React.useState({
    notarizedObject: '',
    notarizedType: '',
    contract: ''
  });

  const handleSubmit = (event) => {
    event.preventDefault();
    const val = {
      notarizedObject: dialogValue.notarizedObject,
      notarizedType: dialogValue.notarizedType,
      contract: dialogValue.contract,
      notaryId: props.user.id
    };
    notarizedObjects.push(val);
    props.handleSelect(val);
    handleClose();
    setValue(val);
    add(val).then((response) => {
      if (response.created) {
        addNotarizedObjectNotifications([
          new Notification(
            locale.notarizedObjectCreateSuccessNotification,
            'success',
            6000
          )
        ]);
      } else {
        addNotarizedObjectNotifications([
          new Notification(
            locale.notarizedObjectCreateErrorNotification,
            'error',
            6000
          )
        ]);
      }
    });
  };

  const handleFieldChange = (event: Event) => {
    if (
      event.target.name === 'contract' &&
      event.target.value === 'contractByDead'
    ) {
      setDialogValue({...dialogValue, notarizedType: 'lawOfSuccession'});
    }
    if (
      event.target.name === 'contract' &&
      event.target.value === 'contractByDead+contractAmongLiving'
    ) {
      setDialogValue({...dialogValue, notarizedType: 'familyInheritanceLaw'});
    }
    /* FIXME: Same if as above?? Furthermore, why no if else?? */
    if (
      event.target.name === 'contract' &&
      event.target.value === 'contractByDead+contractAmongLiving'
    ) {
      setDialogValue({...dialogValue, notarizedType: 'familyAndSoccessionLaw'});
    }
    setDialogValue({...dialogValue, [event.target.name]: event.target.value});
  };

  const handleAutoCompleteChange = (event, newValue) => {
    if (typeof newValue === 'string') {
      // timeout to avoid instant validation of the dialog's form.
      setTimeout(() => {
        toggleOpen(true);
        setDialogValue({
          notarizedObject: newValue,
          notarizedType: '',
          contract: ''
        });
      });
      return;
    }
    if (newValue && newValue.inputValue) {
      toggleOpen(true);
      setDialogValue({
        notarizedObject: newValue.inputValue,
        notarizedType: '',
        contract: ''
      });
      return;
    }
    setValue(newValue);
    props.handleSelect(newValue);
  };

  return (
    <React.Fragment>
      <Autocomplete
        value={value}
        disabled={props.disabled}
        onChange={(event, newValue) =>
          handleAutoCompleteChange(event, newValue)
        }
        filterOptions={(options, params) => {
          const filtered = filter(options, params);
          if (params.inputValue !== '') {
            filtered.push({
              inputValue: params.inputValue,
              notarizedObject: locale.formatString(
                locale.addNotarizedObject,
                params.inputValue
              )
            });
          }
          return filtered;
        }}
        id='notarized-object-auto'
        options={notarizedObjects}
        getOptionLabel={(option) => {
          // e.g value selected with enter, right from the input
          if (typeof option === 'string') {
            return option;
          }
          if (option.inputValue) {
            return option.inputValue;
          }
          return option.notarizedObject;
        }}
        renderOption={(option) => option.notarizedObject}
        style={{width: 300}}
        freeSolo
        renderInput={(params) => (
          <TextField
            required
            disabled={props.disabled}
            error={!props.validNotarizedObject}
            helperText={
              !props.validNotarizedObject ? locale.notarizedObjectEmpty : ''
            }
            {...params}
            margin='normal'
            label={locale.notarizedObject}
            style={styles.textField}
            fullWidth
          />
        )}
      />
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby='notarized-object-dialog-title'
      >
        <form onSubmit={handleSubmit}>
          <DialogTitle id='notarized-object-dialog-title'>
            {locale.notarizedObjectDialogTitle}
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              {locale.notarizedObjectDialogDescription}
            </DialogContentText>
            <TextField
              autoFocus
              margin='dense'
              id='name'
              value={dialogValue.notarizedObject}
              onChange={(event) =>
                setDialogValue({
                  ...dialogValue,
                  notarizedObject: event.target.value
                })
              }
              label={locale.notarizedObject}
              type='text'
              fullWidth
            />
            <ContractRadio
              contract={dialogValue.contract}
              handleFieldChange={(event) => handleFieldChange(event)}
            />
            <NotarizedTypeInput
              notarizedType={dialogValue.notarizedType}
              contract={dialogValue.contract}
              handleFieldChange={(event) => handleFieldChange(event)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} color='primary'>
              {locale.cancelButton}
            </Button>
            <Button
              type='submit'
              color='primary'
              disabled={
                !(
                  dialogValue.notarizedObject &&
                  dialogValue.contract &&
                  dialogValue.notarizedType
                )
              }
            >
              {locale.saveButton}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </React.Fragment>
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ConnectedNotarizedObjectInput);

ConnectedNotarizedObjectInput.propTypes = {
  user: PropTypes.object.isRequired,
  handleSelect: PropTypes.func,
  value: PropTypes.string,
  disabled: PropTypes.bool,
  validNotarizedObject: PropTypes.bool,
  dispatch: PropTypes.func.isRequired
};
